import React, { useEffect, useMemo, useState } from "react";
import { Tab, Tabs } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import AdLayout from "../../components/AdLayout";
import "./accountingResponsive.css";
import RevenueList from "./RevenueList";
import ExpenseList from "./ExpenseList";
import ProfitList from "./ProfitList";
import CashFlowJournal from "./CashFlowJournal";
import AssetReportList from "./AssetReportList";
import axiosInstance from "../../axiosInstance";
import {
  fetchAccountingPermissions,
  ACCOUNTING_REPORT_REVENUE,
  ACCOUNTING_REPORT_EXPENSE,
  ACCOUNTING_REPORT_PROFIT,
  ACCOUNTING_REPORT_ASSETS,
  ACCOUNTING_REPORT_CASHFLOW,
} from "../../libs/customerCreatePermission";

const TAB_FEATURES = [
  { key: "revenue", title: "Doanh thu", feature: ACCOUNTING_REPORT_REVENUE },
  { key: "expense", title: "Chi phí", feature: ACCOUNTING_REPORT_EXPENSE },
  { key: "profit", title: "Lợi nhuận", feature: ACCOUNTING_REPORT_PROFIT },
  { key: "assets", title: "Tài sản", feature: ACCOUNTING_REPORT_ASSETS },
  { key: "cashflow", title: "Dòng tiền", feature: ACCOUNTING_REPORT_CASHFLOW },
];

const ReportManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "revenue";
  const [tabView, setTabView] = useState({});
  const [permLoaded, setPermLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        TAB_FEATURES.map((t) => fetchAccountingPermissions(axiosInstance, t.feature))
      );
      if (cancelled) return;
      const next = {};
      TAB_FEATURES.forEach((t, i) => {
        next[t.key] = Boolean(results[i]?.canView);
      });
      setTabView(next);
      setPermLoaded(true);
    })();
    return () => { cancelled = true; };
  }, []);

  const allowedTabs = useMemo(
    () => TAB_FEATURES.filter((t) => tabView[t.key]).map((t) => t.key),
    [tabView]
  );

  useEffect(() => {
    if (!permLoaded || allowedTabs.length === 0) return;
    if (!allowedTabs.includes(tab)) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", allowedTabs[0]);
      setSearchParams(next, { replace: true });
    }
  }, [permLoaded, allowedTabs, tab, searchParams, setSearchParams]);

  const initialFilters = {
    period: searchParams.get("period") || undefined,
    dateFrom: searchParams.get("dateFrom") || undefined,
    dateTo: searchParams.get("dateTo") || undefined,
    lineId: searchParams.get("lineId") || undefined,
    customerCode: searchParams.get("customerCode") || undefined,
    partnerId: searchParams.get("partnerId") || undefined,
    categoryId: searchParams.get("categoryId") || undefined,
  };

  const handleTabSelect = (key) => {
    if (!key || !allowedTabs.includes(key)) return;
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next);
  };

  if (!permLoaded) return null;

  return (
    <AdLayout>
      <div className="acc-page">
        <h5 className="fw-bold mb-3">Báo cáo</h5>
        <Tabs
          activeKey={allowedTabs.includes(tab) ? tab : allowedTabs[0]}
          onSelect={handleTabSelect}
          className="mb-3 acc-tabs-scroll"
        >
          {tabView.revenue && (
            <Tab eventKey="revenue" title="Doanh thu">
              {tab === "revenue" && <RevenueList embedded initialFilters={initialFilters} />}
            </Tab>
          )}
          {tabView.expense && (
            <Tab eventKey="expense" title="Chi phí">
              {tab === "expense" && <ExpenseList embedded initialFilters={initialFilters} />}
            </Tab>
          )}
          {tabView.profit && (
            <Tab eventKey="profit" title="Lợi nhuận">
              {tab === "profit" && <ProfitList embedded />}
            </Tab>
          )}
          {tabView.assets && (
            <Tab eventKey="assets" title="Tài sản">
              {tab === "assets" && <AssetReportList embedded />}
            </Tab>
          )}
          {tabView.cashflow && (
            <Tab eventKey="cashflow" title="Dòng tiền">
              {tab === "cashflow" && <CashFlowJournal embedded />}
            </Tab>
          )}
        </Tabs>
      </div>
    </AdLayout>
  );
};

export default ReportManagement;
